import { useReactiveVar } from '@apollo/client';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { urlLanguage } from 'apollo';
import LoadingAnimation from 'components/custom/loading-screen/LoadingAnimation';
import {
  GetArticleDocument,
  GetArticleIdDocument,
  GetArticleIdQuery,
  GetArticleQuery,
  GetTagsWithFilteredRelatedArticlesDocument,
  GetTagsWithFilteredRelatedArticlesQuery,
} from 'graphql/__generated/sdk';
import ArticleTemplate from 'pages/Article/ArticleTemplate';
import { mapArticleContent } from 'pages/Article/mappers';
import useI18nLazyQuery from 'utils/hooks/useI18nLazyQuery';
import useI18nQuery from 'utils/hooks/useI18nQuery';
import useRedirectTo404 from 'utils/hooks/useRedirectTo404';
import { useTranslateSlug } from 'utils/hooks/useTranslateSlug';
import { generateLazyQueryOptions } from 'utils/mappers';
import { getEntryId } from 'utils/utils';

export default function Article() {
  const { articleSlug } = useParams();
  const currentUrlLanguage = useReactiveVar(urlLanguage);

  const {
    data: articleIdData,
    loading: articleIdLoading,
    called: articleIdCalled,
  } = useI18nQuery<GetArticleIdQuery>(GetArticleIdDocument, {
    variables: {
      slug: articleSlug,
    },
  });

  const articleId = getEntryId(articleIdData);

  const [getArticle, { data: articleData, loading: articleLoading, called: articleDataCalled }] =
    useI18nLazyQuery<GetArticleQuery>(GetArticleDocument);

  useRedirectTo404(articleId, articleIdLoading);
  useTranslateSlug(articleData?.article || null);

  const [
    getTagsWithFilteredRelatedArticlesQuery,
    { loading: tagsWithFilteredRelatedArticlesLoading, data: tagsWithFilteredRelatedArticlesData },
  ] = useI18nLazyQuery<GetTagsWithFilteredRelatedArticlesQuery>(
    GetTagsWithFilteredRelatedArticlesDocument
  );

  useEffect(() => {
    if (articleData) {
      const queryOptions = generateLazyQueryOptions(articleData.article);

      if (!queryOptions.variables.tagsFilter.length) return;

      void getTagsWithFilteredRelatedArticlesQuery(queryOptions);
    }
  }, [articleData, getTagsWithFilteredRelatedArticlesQuery]);

  useEffect(() => {
    if (articleId) {
      void getArticle({ variables: { id: articleId } });
    }
  }, [getArticle, articleId]);

  if (
    !(articleIdCalled && articleDataCalled) ||
    (articleIdLoading && !articleIdData) ||
    (articleLoading && !articleData) ||
    (tagsWithFilteredRelatedArticlesLoading && !tagsWithFilteredRelatedArticlesData)
  ) {
    return <LoadingAnimation />;
  }

  return (
    <ArticleTemplate
      {...mapArticleContent(
        [articleData!.article, tagsWithFilteredRelatedArticlesData],
        currentUrlLanguage
      )}
    />
  );
}
